import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Tabs,
  Tab,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaCheck,
  FaExclamationTriangle,
  FaEdit,
  FaSave,
  FaSync,
  FaCog,
  FaQuestionCircle,
} from "react-icons/fa";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { invalidarCache } from "../../services/asistencia/asistenciaConfigService";

const TIPOS_TURNO = {
  DIURNO: "diurno",
  VESPERTINO: "vespertino",
  NOCTURNO: "nocturno",
};

const CONFIGURACION_DEFAULT = {
  version: "2.0.1-lite",
  ultimaActualizacion: new Date().toISOString(),
  engine: {
    mode: "lite",
  },
  parametros: {
    // PARÁMETROS GENERALES DE JORNADA Y TIEMPOS
    horasDescansoObligatorio: 8,
    horaInicioTurnoNocturno: 22,
    horaFinTurnoNocturno: 6,
    horasMinimasJornada: 8,
    horasMaximasJornada: 12,
    toleranciaMinutosEntrada: 15,
    toleranciaMinutosSalida: 15,
    distanciaMaximaPermitida: 100,
    precisionMinimaGPS: 50,
    tiempoMinimoRefrigerio: 45,
    tiempoMaximoRefrigerio: 60,
    horarioEntradaPorDefecto: "08:00",
    horarioSalidaPorDefecto: "18:00",
    permitirOlvidosMarcacion: false,
    horaLimiteDeteccionOlvidos: 23,
    horasMaximasParaOlvido: 24,
    refrigerioPermitidoFueraGeocerca: false,

    // PARÁMETROS DEL DETECTOR DE TURNOS
    detectorTurnos: {
      habilitado: true,
      modoDeteccion: "automatico",
      tipoTurnoPorDefecto: TIPOS_TURNO.DIURNO,
      criteriosDeteccion: {
        [TIPOS_TURNO.DIURNO]: {
          rangoEntrada: { min: 6, max: 10 },
          rangoSalida: { min: 15, max: 20 },
          duracionMinima: 6,
          duracionMaxima: 12,
          cruzaMedianoche: false,
        },
        [TIPOS_TURNO.VESPERTINO]: {
          rangoEntrada: { min: 13, max: 18 },
          rangoSalida: { min: 21, max: 2 },
          duracionMinima: 6,
          duracionMaxima: 10,
          cruzaMedianoche: true,
        },
        [TIPOS_TURNO.NOCTURNO]: {
          rangoEntrada: { min: 20, max: 24 },
          rangoSalida: { min: 3, max: 10 },
          duracionMinima: 6,
          duracionMaxima: 12,
          cruzaMedianoche: true,
        },
      },
      manejoJornadas: {
        horaCorteJornada: "05:30", // Cambiado de número a string
        usarFechaJornada: true,
        permitirCruceDeFechas: true,
        detectarTurnoAutomatico: true,
        umbralDeteccionNocturno: 15,
      },
    },
  },
  reglas: {
    permitirIngresoDespuesDeSalida: false,
    permitirIngresoSinDistancia: false,
    permitirSalidaSinDistancia: false,
    permitirRefrigerioSinDistancia: false,
    validacionUbicacion: {
      habilitada: true,
      permitirFueraDeArea: false,
      bloquearFueraDeArea: false,
      alertarFueraDeArea: true,
    },
    validacionHorarios: {
      habilitada: true,
      permitirEntradaTemprana: false,
      permitirEntradaTardia: true,
      permitirSalidaTemprana: false,
      permitirSalidaTardia: true,
    },
    validacionSecuencial: {
      habilitada: true,
      permitirDuplicados: false,
      validarOrdenLogico: true,
      tiempoMinimoEntreMarcaciones: 5,
      usarFechaJornada: true,
      ventanaValidacion: "inteligente",
      adaptarSegunTipoTurno: true,
    },
    validacionRefrigerio: {
      habilitada: true,
      requiereEntradaPrevia: true,
      tiempoMinimoTrabajado: 120,
      soloUnRefrigerioPorJornada: true,
    },
    validacionPorTipoTurno: {
      habilitada: true,
      adaptarHorariosSegunTurno: true,
      validarDuracionJornada: true,
    },
  },
  flujosMarcacion: {
    secuenciaNormal: [
      "Ingreso",
      "Refrigerio_Inicio",
      "Refrigerio_Fin",
      "Salida",
    ],
    secuenciaSinRefrigerio: ["Ingreso", "Salida"],
    permitirSaltarRefrigerio: false,
    flujosPorTipoTurno: {
      [TIPOS_TURNO.DIURNO]: [
        "Ingreso",
        "Refrigerio_Inicio",
        "Refrigerio_Fin",
        "Salida",
      ],
      [TIPOS_TURNO.VESPERTINO]: [
        "Ingreso",
        "Refrigerio_Inicio",
        "Refrigerio_Fin",
        "Salida",
      ],
      [TIPOS_TURNO.NOCTURNO]: [
        "Ingreso",
        "Refrigerio_Inicio",
        "Refrigerio_Fin",
        "Salida",
      ],
    },
  },
  mensajes: {
    bienvenida: "Bienvenido al control de asistencia.",
    despedida: "Gracias por registrar tu salida.",
    refrigerio: "Buen provecho.",
    errorUbicacionNoDisponible: "No se pudo obtener la ubicación.",
    errorTurnoCompleto: "Ya completó su turno.",
    errorDescansoInsuficiente: "No cumplió el descanso obligatorio.",
    firmaIngreso: "Estás firmando tu asistencia de ingreso.",
    firmaSalida: "Estás firmando tu asistencia de salida.",
    refrigerioRango: "El refrigerio debe durar entre {min} y {max} minutos.",
  },
  botones: {
    Ingreso: { label: "Ingresar", color: "#4CAF50", icon: "login" },
    Salida: { label: "Salir", color: "#F44336", icon: "logout" },
    Refrigerio_Inicio: { label: "Inic. Refri", color: "#FF9800", icon: "restaurant" },
    Refrigerio_Fin: { label: "Fin Refri", color: "#FFC107", icon: "restaurant_menu" },
  },
  lite: {
    transiciones: {
      ingresoARefrigerio: { minutosMinimos: 120, modo: "advertencia" },
      refrigerioFinASalida: { minutosMinimos: 120, modo: "advertencia" },
      ingresoASalida: { minutosMinimos: 480, modo: "advertencia" },
    },
    refrigerio: {
      minimoLegalMinutos: 45,
      maximoMinutos: 60,
      duracionCeroModo: "bloqueo",
      menorAlMinimoModo: "bloqueo",
      mayorAlMaximoModo: "bloqueo",
      ordenModo: "bloqueo",
      horaInicioSugerida: "12:00",
    },
    secuencia: {
      tipoIncorrectoModo: "advertencia",
      duplicadoModo: "advertencia",
      ordenLogicoModo: "advertencia",
      tiempoMinimoEntreMarcacionesModo: "advertencia",
      salidaSinRefrigerioModo: "advertencia",
      priorizarSalidaSobreRefrigerio: true,
    },
    horarios: {
      entradaTempranaModo: "advertencia",
      entradaTardiaModo: "advertencia",
      salidaTempranaModo: "advertencia",
      salidaTardiaModo: "advertencia",
    },
    jornada: {
      minimaModo: "advertencia",
      maximaModo: "advertencia",
    },
    politicasPorCentro: {},
    turnos: {
      nocturno: {
        habilitado: false,
        aplicarATodos: false,
        usuariosDni: [],
        centros: [],
        horaCorteJornada: 12,
      },
    },
    rolTurnos: {
      habilitado: false,
      bloquearSinRol: false,
      modo: "bloqueo",
    },
  },
};

const PARAMETROS_HELP_TEXTS = {
  horasDescansoObligatorio: "Horas mínimas que deben transcurrir entre el fin de una jornada y el inicio de la siguiente.",
  horaInicioTurnoNocturno: "Hora (0-23) a partir de la cual se considera que inicia un turno nocturno para cálculos.",
  horaFinTurnoNocturno: "Hora (0-23) en la que finaliza la consideración de turno nocturno.",
  horasMinimasJornada: "Duración mínima en horas que debe tener una jornada laboral completa.",
  horasMaximasJornada: "Duración máxima permitida para una jornada laboral.",
  toleranciaMinutosEntrada: "Minutos de tolerancia permitidos para marcar la entrada después de la hora teórica.",
  toleranciaMinutosSalida: "Minutos de tolerancia permitidos para marcar la salida antes de la hora teórica.",
  distanciaMaximaPermitida: "Distancia máxima (en metros) del centro de trabajo para considerar una marcación como válida (si la validación de ubicación está activa).",
  precisionMinimaGPS: "Precisión mínima (en metros) requerida de la señal GPS para considerar la ubicación como fiable.",
  tiempoMinimoRefrigerio: "Duración mínima en minutos que debe tener el periodo de refrigerio.",
  tiempoMaximoRefrigerio: "Duración máxima en minutos permitida para el periodo de refrigerio.",
  horarioEntradaPorDefecto: "Horario de entrada predeterminado si no se especifica uno.",
  horarioSalidaPorDefecto: "Horario de salida predeterminado si no se especifica uno.",
  permitirOlvidosMarcacion: "Permite a los usuarios registrar marcaciones olvidadas.",
  horaLimiteDeteccionOlvidos: "Hora límite del día para detectar y registrar olvidos de marcación.",
  horasMaximasParaOlvido: "Número máximo de horas hacia atrás para permitir el registro de un olvido.",
  refrigerioPermitidoFueraGeocerca: "Permite que el refrigerio se marque fuera de la geocerca definida.",
  "detectorTurnos.habilitado": "Indica si el sistema intentará detectar automáticamente el tipo de turno del empleado.",
  "detectorTurnos.modoDeteccion": "Modo de detección ('automatico', 'manual', 'fijo').",
  "detectorTurnos.tipoTurnoPorDefecto": "Tipo de turno a asumir si la detección falla o está deshabilitada.",
  "detectorTurnos.manejoJornadas.horaCorteJornada": "Hora (0-23) utilizada para determinar a qué día pertenece una marcación (ej. marcación a las 2 AM puede pertenecer a la jornada del día anterior si el corte es a las 3 AM).",
  "detectorTurnos.manejoJornadas.usarFechaJornada": "Habilita la lógica de agrupar marcaciones por \"fecha de jornada\" en lugar de fecha calendario estricta.",
  "detectorTurnos.manejoJornadas.permitirCruceDeFechas": "Permite que una jornada laboral se extienda más allá de la medianoche (ej. turno nocturno).",
  "detectorTurnos.manejoJornadas.detectarTurnoAutomatico": "Intenta detectar el turno automáticamente basado en la hora de marcación.",
  "detectorTurnos.manejoJornadas.umbralDeteccionNocturno": "Hora a partir de la cual se considera una marcación como potencialmente nocturna para la lógica de fecha de jornada.",
};

const REGLAS_HELP_TEXTS = {
  permitirIngresoDespuesDeSalida: "Permite marcar un nuevo ingreso después de haber marcado una salida en la misma jornada.",
  permitirIngresoSinDistancia: "Permite el ingreso sin validar la distancia a la geocerca.",
  permitirSalidaSinDistancia: "Permite la salida sin validar la distancia a la geocerca.",
  permitirRefrigerioSinDistancia: "Permite marcar inicio/fin de refrigerio sin validar la distancia a la geocerca.",
  "validacionUbicacion.habilitada": "Activa o desactiva la validación de ubicación (geocerca, distancia).",
  "validacionUbicacion.permitirFueraDeArea": "Permite registrar marcaciones incluso si están fuera del área permitida (puede generar advertencias).",
  "validacionUbicacion.bloquearFueraDeArea": "Bloquea completamente el registro si la marcación está fuera del área (si es true, 'permitirFueraDeArea' usualmente es false).",
  "validacionUbicacion.alertarFueraDeArea": "Genera una advertencia si la marcación está fuera del área, pero permite el registro si 'permitirFueraDeArea' es true.",
  "validacionHorarios.habilitada": "Activa o desactiva la validación de horarios (temprano, tarde, duración).",
  "validacionHorarios.permitirEntradaTemprana": "Permite marcar la entrada antes de la hora teórica del turno.",
  "validacionHorarios.permitirEntradaTardia": "Permite marcar la entrada después de la hora teórica (puede generar advertencias o descuentos según otras lógicas).",
  "validacionHorarios.permitirSalidaTemprana": "Permite marcar la salida antes de la hora teórica de fin de turno.",
  "validacionHorarios.permitirSalidaTardia": "Permite marcar la salida después de la hora teórica de fin de turno.",
  "validacionHorarios.validacionPorTipoTurno.habilitada": "Habilita validaciones específicas basadas en el tipo de turno detectado.",
  "validacionHorarios.validacionPorTipoTurno.adaptarHorariosSegunTurno": "Ajusta las expectativas de horario según el tipo de turno.",
  "validacionHorarios.validacionPorTipoTurno.validarDuracionJornada": "Valida si la jornada cumple con las horas mínimas/máximas configuradas.",
  "validacionSecuencial.habilitada": "Activa o desactiva la validación del orden lógico de las marcaciones (Ingreso -> Refrigerio -> Salida).",
  "validacionSecuencial.permitirDuplicados": "Permite registrar marcaciones del mismo tipo consecutivas (ej. dos \"Ingreso\" seguidos).",
  "validacionSecuencial.validarOrdenLogico": "Verifica que las marcaciones sigan una secuencia lógica.",
  "validacionSecuencial.tiempoMinimoEntreMarcaciones": "Tiempo mínimo en minutos que debe transcurrir entre dos marcaciones del mismo tipo (si se permiten duplicados) o entre marcaciones diferentes si aplica.",
  "validacionSecuencial.usarFechaJornada": "Aplica la validación secuencial dentro del contexto de una \"fecha de jornada\".",
  "validacionSecuencial.ventanaValidacion": "Define cómo se agrupan las marcaciones para la validación secuencial ('dia_calendario', 'jornada', 'inteligente').",
  "validacionSecuencial.adaptarSegunTipoTurno": "Adapta la secuencia esperada según el tipo de turno.",
  "validacionRefrigerio.habilitada": "Activa o desactiva las validaciones específicas para el refrigerio.",
  "validacionRefrigerio.requiereEntradaPrevia": "Exige que se haya marcado \"Ingreso\" antes de poder marcar \"Refrigerio_Inicio\".",
  "validacionRefrigerio.tiempoMinimoTrabajado": "Minutos mínimos que se deben haber trabajado (desde el Ingreso) antes de poder iniciar el refrigerio.",
  "validacionRefrigerio.soloUnRefrigerioPorJornada": "Limita a un solo ciclo de refrigerio (Inicio y Fin) por jornada.",
};

function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item);
}

function deepMerge(target, source) {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key]) && key in target && isObject(target[key])) {
        output[key] = deepMerge(target[key], source[key]);
      } else {
        output[key] = source[key];
      }
    });
    Object.keys(target).forEach(key => {
      if (!(key in output)) {
        output[key] = target[key];
      }
    });
  }
  return output;
}

const HelpTooltip = ({ text, id }) => {
  if (!text) return null;
  return (
    <OverlayTrigger
      placement="right"
      delay={{ show: 250, hide: 400 }}
      overlay={<Tooltip id={`tooltip-${id}`}>{text}</Tooltip>}
    >
      <span tabIndex={0} className="ms-1" style={{ display: 'inline-block', cursor: 'help' }}>
        <FaQuestionCircle style={{ color: "#6c757d", fontSize: "0.9em" }} />
      </span>
    </OverlayTrigger>
  );
};

const ConfiguracionAsistencia = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "global";
  const [tabIndex, setTabIndex] = useState("parametros");
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(true);
  const [configActual, setConfigActual] = useState(null);

  const cargarConfiguracion = async () => {
    setCargando(true);
    setError("");
    setMensaje("");
    try {
      const db = getFirestore();
      const docRef = doc(db, "asistencia-config", rucEmpresa);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const fetchedConfig = docSnap.data();
        const mergedConfig = deepMerge(JSON.parse(JSON.stringify(CONFIGURACION_DEFAULT)), fetchedConfig);
        setConfigActual(mergedConfig);
      } else {
        setConfigActual(JSON.parse(JSON.stringify(CONFIGURACION_DEFAULT)));
      }
    } catch (err) {
      setError("Error al cargar configuración: " + err.message);
      setConfigActual(JSON.parse(JSON.stringify(CONFIGURACION_DEFAULT)));
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (rucEmpresa) {
      cargarConfiguracion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rucEmpresa]);

  const actualizarValorAnidado = (seccion, path, valor) => {
    setConfigActual((prev) => {
      if (!prev) return null;
      const newConfig = JSON.parse(JSON.stringify(prev));

      const keys = path.split(".");
      let current = newConfig[seccion];

      if (!current) {
        newConfig[seccion] = {};
        current = newConfig[seccion];
      }

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = valor;
      return newConfig;
    });
  };

  const guardarConfiguracion = async () => {
    setGuardando(true);
    setMensaje("");
    setError("");
    try {
      const db = getFirestore();
      // Incrementar versión en 0.01 (patch +1) manteniendo el sufijo "-lite"
      const incrementarVersion = (versionActual) => {
        try {
          const [numerica, ...sufijos] = String(versionActual || "2.0.0-lite").split("-");
          const partes = numerica.split(".").map(Number);
          partes[partes.length - 1] = (partes[partes.length - 1] || 0) + 1;
          const nuevaNumerica = partes.join(".");
          return sufijos.length ? `${nuevaNumerica}-${sufijos.join("-")}` : nuevaNumerica;
        } catch {
          return versionActual;
        }
      };

      const configParaGuardar = {
        ...configActual,
        version: incrementarVersion(configActual?.version),
        ultimaActualizacion: new Date().toISOString(),
        engine: configActual.engine || CONFIGURACION_DEFAULT.engine,
      };
      await setDoc(
        doc(db, "asistencia-config", rucEmpresa),
        configParaGuardar
      );
      invalidarCache(rucEmpresa);
      setMensaje("¡Configuración guardada correctamente!");
      setEditando(false);
      setConfigActual(configParaGuardar);
    } catch (err) {
      setError("Error al guardar: " + err.message);
    } finally {
      setGuardando(false);
    }
  };

  const isLiteMode = String(configActual?.engine?.mode || "").toLowerCase() === "lite";

  useEffect(() => {
    if (!isLiteMode && tabIndex === "lite") {
      setTabIndex("parametros");
    }
  }, [isLiteMode, tabIndex]);

  if (cargando || !configActual) {
    return (
      <Container className="py-4">
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Cargando configuración...</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const parametrosSafe = configActual.parametros || CONFIGURACION_DEFAULT.parametros;
  const reglasSafe = configActual.reglas || CONFIGURACION_DEFAULT.reglas;
  const flujosSafe = configActual.flujosMarcacion || CONFIGURACION_DEFAULT.flujosMarcacion;
  const mensajesSafe = configActual.mensajes || CONFIGURACION_DEFAULT.mensajes;
  const botonesSafe = configActual.botones || CONFIGURACION_DEFAULT.botones;
  const liteSafe = configActual.lite || CONFIGURACION_DEFAULT.lite;

  return (
    <Container className="py-4">
      <Card
        className="shadow-lg border-0 mb-4"
        style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}
      >
        {/* Header Premium con degradado verde oscuro */}
        <Card.Header
          className="text-white py-4 position-relative"
          style={{
            background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
            borderBottom: "3px solid #f39c12",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader usuario={usuario} />
          </div>
          <div className="text-center">
            <h2 className="fw-bold mb-1">
              <FaCog className="me-3" />
              Configuración de Asistencia
            </h2>
            <p className="mb-0 opacity-75">
              Personaliza el motor de validaciones y parámetros globales | RUC:{" "}
              {usuario?.ruc || "–"}
            </p>
          </div>
        </Card.Header>

        <Card.Body>
          {/* Acciones Rápidas */}
          <Card
            className="border-0 mb-4"
            style={{
              background: "linear-gradient(135deg,#fffcea,#fff9db)",
              borderRadius: 14,
              boxShadow: "0 2px 12px rgba(109,40,217,.07)",
            }}
          >
            <Card.Body className="p-3 d-flex justify-content-center gap-3 flex-wrap">
              <Button
                variant={editando ? "outline-danger" : "outline-dark"}
                onClick={() => setEditando((e) => !e)}
                className="d-flex align-items-center gap-2 bg-white"
                style={{
                  borderRadius: "10px",
                  padding: "10px 20px",
                  fontWeight: "bold",
                  border: editando ? "2px solid #dc3545" : "1px solid #dee2e6",
                }}
              >
                <FaEdit size="16px" />
                {editando ? "Cancelar Edición" : "Editar Configuración"}
              </Button>
              <Button
                variant="outline-dark"
                onClick={cargarConfiguracion}
                disabled={cargando || guardando}
                className="d-flex align-items-center gap-2 bg-white"
                style={{
                  borderRadius: "10px",
                  padding: "10px 20px",
                  fontWeight: "bold",
                }}
              >
                <FaSync className={cargando ? "fa-spin" : ""} size="16px" />
                Recargar
              </Button>
              {editando && (
                <Button
                  variant="success"
                  onClick={guardarConfiguracion}
                  disabled={guardando}
                  className="d-flex align-items-center gap-2"
                  style={{
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    border: "none",
                    padding: "10px 20px",
                    fontWeight: "bold",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  <FaSave size="16px" />
                  {guardando ? "Guardando..." : "Guardar Cambios"}
                </Button>
              )}
            </Card.Body>
          </Card>

          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible className="d-flex align-items-center">
              <FaExclamationTriangle className="me-2" /> <div>{error}</div>
            </Alert>
          )}
          {mensaje && (
            <Alert variant="success" onClose={() => setMensaje("")} dismissible className="d-flex align-items-center">
              <FaCheck className="me-2" /> <div>{mensaje}</div>
            </Alert>
          )}

          <Tabs activeKey={tabIndex} onSelect={(k) => setTabIndex(k)} className="mb-3 nav-tabs-custom">
            <Tab eventKey="parametros" title="Parámetros">
              <ParametrosForm
                parametros={parametrosSafe}
                onUpdate={(path, valor) => actualizarValorAnidado("parametros", path, valor)}
                editable={editando}
              />
            </Tab>
            <Tab eventKey="reglas" title="Reglas">
              <ReglasForm
                reglas={reglasSafe}
                onUpdate={(path, valor) => actualizarValorAnidado("reglas", path, valor)}
                editable={editando}
              />
            </Tab>
            <Tab eventKey="flujos" title="Flujos de Marcación">
              <FlujosMarcacionForm
                flujos={flujosSafe}
                onUpdate={(path, valor) => actualizarValorAnidado("flujosMarcacion", path, valor)}
                editable={editando}
              />
            </Tab>
            <Tab eventKey="mensajes" title="Mensajes">
              <MensajesForm
                mensajes={mensajesSafe}
                onUpdate={(path, valor) => actualizarValorAnidado("mensajes", path, valor)}
                editable={editando}
              />
            </Tab>
            <Tab eventKey="botones" title="Botones">
              <BotonesForm
                botones={botonesSafe}
                onUpdate={(path, valor) => actualizarValorAnidado("botones", path, valor)}
                editable={editando}
              />
            </Tab>
            {isLiteMode && (
              <Tab eventKey="lite" title="Motor Lite">
                <LiteForm
                  lite={liteSafe}
                  onUpdate={(path, valor) => actualizarValorAnidado("lite", path, valor)}
                  editable={editando}
                />
              </Tab>
            )}
          </Tabs>
          {editando && (
            <div className="text-end mt-3">
              <Button variant="success" onClick={guardarConfiguracion} disabled={guardando}>
                <FaSave className="me-2" /> {guardando ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

const JsonTextArea = ({ value, onChange, disabled, rows = 5 }) => {
  const [textValue, setTextValue] = useState(JSON.stringify(value, null, 2));
  const [jsonError, setJsonError] = useState("");

  useEffect(() => {
    setTextValue(JSON.stringify(value, null, 2));
  }, [value]);

  const handleChange = (e) => {
    const newText = e.target.value;
    setTextValue(newText);
    try {
      const parsed = JSON.parse(newText);
      onChange(parsed);
      setJsonError("");
    } catch (err) {
      setJsonError("JSON inválido: " + err.message);
    }
  };

  return (
    <>
      <Form.Control
        as="textarea"
        rows={rows}
        value={textValue}
        onChange={handleChange}
        disabled={disabled}
        isInvalid={!!jsonError}
      />
      {jsonError && <Form.Control.Feedback type="invalid">{jsonError}</Form.Control.Feedback>}
    </>
  );
};

const ParametrosForm = ({ parametros, onUpdate, editable }) => {
  const detectorTurnos = parametros.detectorTurnos || CONFIGURACION_DEFAULT.parametros.detectorTurnos;
  const manejoJornadas = detectorTurnos.manejoJornadas || CONFIGURACION_DEFAULT.parametros.detectorTurnos.manejoJornadas;
  const [subTabIndex, setSubTabIndex] = useState("generales");

  const getComment = (path) => {
    return PARAMETROS_HELP_TEXTS[path] || undefined;
  };

  return (
    <div>
      <Tabs activeKey={subTabIndex} onSelect={(k) => setSubTabIndex(k)} className="mb-3">
        <Tab eventKey="generales" title="Generales">
          <Form>
            <Row className="mb-3">
              <Col md={4}><Form.Group><Form.Label>Horas descanso obligatorio <HelpTooltip id="hdo" text={getComment("horasDescansoObligatorio")} /></Form.Label><Form.Control type="number" value={parametros.horasDescansoObligatorio || 0} onChange={(e) => onUpdate("horasDescansoObligatorio", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Hora inicio turno nocturno <HelpTooltip id="hitn" text={getComment("horaInicioTurnoNocturno")} /></Form.Label><Form.Control type="number" value={parametros.horaInicioTurnoNocturno || 0} onChange={(e) => onUpdate("horaInicioTurnoNocturno", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Hora fin turno nocturno <HelpTooltip id="hftn" text={getComment("horaFinTurnoNocturno")} /></Form.Label><Form.Control type="number" value={parametros.horaFinTurnoNocturno || 0} onChange={(e) => onUpdate("horaFinTurnoNocturno", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}><Form.Group><Form.Label>Horas mínimas jornada <HelpTooltip id="hmj" text={getComment("horasMinimasJornada")} /></Form.Label><Form.Control type="number" value={parametros.horasMinimasJornada || 0} onChange={(e) => onUpdate("horasMinimasJornada", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Horas máximas jornada <HelpTooltip id="hmxj" text={getComment("horasMaximasJornada")} /></Form.Label><Form.Control type="number" value={parametros.horasMaximasJornada || 0} onChange={(e) => onUpdate("horasMaximasJornada", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Tolerancia Entrada (min) <HelpTooltip id="tme" text={getComment("toleranciaMinutosEntrada")} /></Form.Label><Form.Control type="number" value={parametros.toleranciaMinutosEntrada || 0} onChange={(e) => onUpdate("toleranciaMinutosEntrada", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}><Form.Group><Form.Label>Tolerancia Salida (min) <HelpTooltip id="tms" text={getComment("toleranciaMinutosSalida")} /></Form.Label><Form.Control type="number" value={parametros.toleranciaMinutosSalida || 0} onChange={(e) => onUpdate("toleranciaMinutosSalida", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Distancia Máxima (m) <HelpTooltip id="dmp" text={getComment("distanciaMaximaPermitida")} /></Form.Label><Form.Control type="number" value={parametros.distanciaMaximaPermitida || 0} onChange={(e) => onUpdate("distanciaMaximaPermitida", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Precisión Mínima GPS (m) <HelpTooltip id="pmg" text={getComment("precisionMinimaGPS")} /></Form.Label><Form.Control type="number" value={parametros.precisionMinimaGPS || 0} onChange={(e) => onUpdate("precisionMinimaGPS", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}><Form.Group><Form.Label>Tiempo mínimo refrigerio (min) <HelpTooltip id="tmr" text={getComment("tiempoMinimoRefrigerio")} /></Form.Label><Form.Control type="number" value={parametros.tiempoMinimoRefrigerio || 0} onChange={(e) => onUpdate("tiempoMinimoRefrigerio", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Tiempo máximo refrigerio (min) <HelpTooltip id="tmxr" text={getComment("tiempoMaximoRefrigerio")} /></Form.Label><Form.Control type="number" value={parametros.tiempoMaximoRefrigerio || 0} onChange={(e) => onUpdate("tiempoMaximoRefrigerio", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Horario entrada por defecto <HelpTooltip id="hepd" text={getComment("horarioEntradaPorDefecto")} /></Form.Label><Form.Control type="text" value={parametros.horarioEntradaPorDefecto || ""} onChange={(e) => onUpdate("horarioEntradaPorDefecto", e.target.value)} disabled={!editable} /></Form.Group></Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}><Form.Group><Form.Label>Horario salida por defecto <HelpTooltip id="hspd" text={getComment("horarioSalidaPorDefecto")} /></Form.Label><Form.Control type="text" value={parametros.horarioSalidaPorDefecto || ""} onChange={(e) => onUpdate("horarioSalidaPorDefecto", e.target.value)} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Permitir olvidos marcación <HelpTooltip id="pom" text={getComment("permitirOlvidosMarcacion")} /></Form.Label><Form.Check type="switch" id="permitirOlvidosMarcacion" label=" " checked={!!parametros.permitirOlvidosMarcacion} onChange={(e) => onUpdate("permitirOlvidosMarcacion", e.target.checked)} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Hora límite detección olvidos <HelpTooltip id="hldo" text={getComment("horaLimiteDeteccionOlvidos")} /></Form.Label><Form.Control type="number" value={parametros.horaLimiteDeteccionOlvidos || 0} onChange={(e) => onUpdate("horaLimiteDeteccionOlvidos", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}><Form.Group><Form.Label>Horas máximas para olvido <HelpTooltip id="hmpo" text={getComment("horasMaximasParaOlvido")} /></Form.Label><Form.Control type="number" value={parametros.horasMaximasParaOlvido || 0} onChange={(e) => onUpdate("horasMaximasParaOlvido", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Refrigerio permitido fuera geocerca <HelpTooltip id="rpfg" text={getComment("refrigerioPermitidoFueraGeocerca")} /></Form.Label><Form.Check type="switch" id="refrigerioPermitidoFueraGeocerca" label=" " checked={!!parametros.refrigerioPermitidoFueraGeocerca} onChange={(e) => onUpdate("refrigerioPermitidoFueraGeocerca", e.target.checked)} disabled={!editable} /></Form.Group></Col>
            </Row>
          </Form>
        </Tab>

        <Tab eventKey="detector" title="Detector de Turnos">
          <Form>
            <Row className="mb-3">
              <Col md={4}><Form.Group><Form.Label>Habilitado <HelpTooltip id="dth" text={getComment("detectorTurnos.habilitado")} /></Form.Label><Form.Check type="switch" id="detectorTurnosHabilitado" label=" " checked={!!detectorTurnos.habilitado} onChange={(e) => onUpdate("detectorTurnos.habilitado", e.target.checked)} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Modo Detección <HelpTooltip id="dtmd" text={getComment("detectorTurnos.modoDeteccion")} /></Form.Label><Form.Control type="text" value={detectorTurnos.modoDeteccion || ""} onChange={(e) => onUpdate("detectorTurnos.modoDeteccion", e.target.value)} disabled={!editable} /></Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Tipo Turno por Defecto <HelpTooltip id="dttpd" text={getComment("detectorTurnos.tipoTurnoPorDefecto")} /></Form.Label>
                <Form.Control as="select" value={detectorTurnos.tipoTurnoPorDefecto || ""} onChange={(e) => onUpdate("detectorTurnos.tipoTurnoPorDefecto", e.target.value)} disabled={!editable}>
                  {Object.values(TIPOS_TURNO).map(tipo => <option key={tipo} value={tipo}>{tipo.charAt(0).toUpperCase() + tipo.slice(1)}</option>)}
                </Form.Control>
              </Form.Group></Col>
            </Row>

            <h6 className="mt-3">Manejo de Jornadas</h6>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Hora Corte Jornada <HelpTooltip id="dtmjhcj" text={getComment("detectorTurnos.manejoJornadas.horaCorteJornada")} /></Form.Label>
                  <Form.Control
                    type="time"
                    value={manejoJornadas.horaCorteJornada || "05:30"}
                    onChange={(e) => onUpdate("detectorTurnos.manejoJornadas.horaCorteJornada", e.target.value)}
                    disabled={!editable}
                  />
                </Form.Group>
              </Col>
              <Col md={3}><Form.Group><Form.Label>Usar Fecha Jornada <HelpTooltip id="dtmjufj" text={getComment("detectorTurnos.manejoJornadas.usarFechaJornada")} /></Form.Label><Form.Check type="switch" id="manejoUsarFechaJornada" label=" " checked={!!manejoJornadas.usarFechaJornada} onChange={(e) => onUpdate("detectorTurnos.manejoJornadas.usarFechaJornada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
              <Col md={3}><Form.Group><Form.Label>Permitir Cruce De Fechas <HelpTooltip id="dtmjpcdf" text={getComment("detectorTurnos.manejoJornadas.permitirCruceDeFechas")} /></Form.Label><Form.Check type="switch" id="manejoPermitirCruce" label=" " checked={!!manejoJornadas.permitirCruceDeFechas} onChange={(e) => onUpdate("detectorTurnos.manejoJornadas.permitirCruceDeFechas", e.target.checked)} disabled={!editable} /></Form.Group></Col>
              <Col md={3}><Form.Group><Form.Label>Detectar Turno Automático <HelpTooltip id="dtmjdta" text={getComment("detectorTurnos.manejoJornadas.detectarTurnoAutomatico")} /></Form.Label><Form.Check type="switch" id="manejoDetectarAuto" label=" " checked={!!manejoJornadas.detectarTurnoAutomatico} onChange={(e) => onUpdate("detectorTurnos.manejoJornadas.detectarTurnoAutomatico", e.target.checked)} disabled={!editable} /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={3}><Form.Group><Form.Label>Umbral Detección Nocturno <HelpTooltip id="dtmjudn" text={getComment("detectorTurnos.manejoJornadas.umbralDeteccionNocturno")} /></Form.Label><Form.Control type="number" value={manejoJornadas.umbralDeteccionNocturno || 0} onChange={(e) => onUpdate("detectorTurnos.manejoJornadas.umbralDeteccionNocturno", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
            </Row>
          </Form>
        </Tab>

        <Tab eventKey="diurno" title="Turno Diurno">
          <TurnoForm
            turno="diurno"
            criterios={detectorTurnos.criteriosDeteccion?.diurno || CONFIGURACION_DEFAULT.parametros.detectorTurnos.criteriosDeteccion.diurno}
            onUpdate={(path, value) => onUpdate(`detectorTurnos.criteriosDeteccion.diurno.${path}`, value)}
            editable={editable}
          />
        </Tab>

        <Tab eventKey="vespertino" title="Turno Vespertino">
          <TurnoForm
            turno="vespertino"
            criterios={detectorTurnos.criteriosDeteccion?.vespertino || CONFIGURACION_DEFAULT.parametros.detectorTurnos.criteriosDeteccion.vespertino}
            onUpdate={(path, value) => onUpdate(`detectorTurnos.criteriosDeteccion.vespertino.${path}`, value)}
            editable={editable}
          />
        </Tab>

        <Tab eventKey="nocturno" title="Turno Nocturno">
          <TurnoForm
            turno="nocturno"
            criterios={detectorTurnos.criteriosDeteccion?.nocturno || CONFIGURACION_DEFAULT.parametros.detectorTurnos.criteriosDeteccion.nocturno}
            onUpdate={(path, value) => onUpdate(`detectorTurnos.criteriosDeteccion.nocturno.${path}`, value)}
            editable={editable}
          />
        </Tab>
      </Tabs>
    </div>
  );
};

const TurnoForm = ({ turno, criterios, onUpdate, editable }) => {
  const turnoLabel = {
    diurno: "Diurno",
    vespertino: "Vespertino",
    nocturno: "Nocturno",
  };

  return (
    <Form>
      <h5 className="mb-3">Configuración de Turno {turnoLabel[turno]}</h5>

      <Row className="mb-3">
        <Col md={6}>
          <h6>Rango de Entrada</h6>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Hora Mínima <HelpTooltip id={`${turno}_entrada_min`} text="Hora mínima para considerar esta marcación como entrada del turno" /></Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="23"
                  value={criterios?.rangoEntrada?.min || 0}
                  onChange={(e) => onUpdate("rangoEntrada.min", Number(e.target.value))}
                  disabled={!editable}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Hora Máxima <HelpTooltip id={`${turno}_entrada_max`} text="Hora máxima para considerar esta marcación como entrada del turno" /></Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="23"
                  value={criterios?.rangoEntrada?.max || 0}
                  onChange={(e) => onUpdate("rangoEntrada.max", Number(e.target.value))}
                  disabled={!editable}
                />
              </Form.Group>
            </Col>
          </Row>
        </Col>

        <Col md={6}>
          <h6>Rango de Salida</h6>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Hora Mínima <HelpTooltip id={`${turno}_salida_min`} text="Hora mínima para considerar esta marcación como salida del turno" /></Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="23"
                  value={criterios?.rangoSalida?.min || 0}
                  onChange={(e) => onUpdate("rangoSalida.min", Number(e.target.value))}
                  disabled={!editable}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Hora Máxima <HelpTooltip id={`${turno}_salida_max`} text="Hora máxima para considerar esta marcación como salida del turno" /></Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="23"
                  value={criterios?.rangoSalida?.max || 0}
                  onChange={(e) => onUpdate("rangoSalida.max", Number(e.target.value))}
                  disabled={!editable}
                />
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Duración Mínima (horas) <HelpTooltip id={`${turno}_duracion_min`} text="Duración mínima en horas para este tipo de turno" /></Form.Label>
            <Form.Control
              type="number"
              value={criterios?.duracionMinima || 0}
              onChange={(e) => onUpdate("duracionMinima", Number(e.target.value))}
              disabled={!editable}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Duración Máxima (horas) <HelpTooltip id={`${turno}_duracion_max`} text="Duración máxima en horas para este tipo de turno" /></Form.Label>
            <Form.Control
              type="number"
              value={criterios?.duracionMaxima || 0}
              onChange={(e) => onUpdate("duracionMaxima", Number(e.target.value))}
              disabled={!editable}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Cruza Medianoche <HelpTooltip id={`${turno}_cruza_medianoche`} text="Indica si este turno típicamente cruza la medianoche" /></Form.Label>
            <Form.Check
              type="switch"
              label=" "
              checked={!!criterios?.cruzaMedianoche}
              onChange={(e) => onUpdate("cruzaMedianoche", e.target.checked)}
              disabled={!editable}
            />
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

const ReglasForm = ({ reglas, onUpdate, editable }) => {
  const validacionUbicacion = reglas.validacionUbicacion || CONFIGURACION_DEFAULT.reglas.validacionUbicacion;
  const validacionHorarios = reglas.validacionHorarios || CONFIGURACION_DEFAULT.reglas.validacionHorarios;
  const validacionPorTipoTurno = reglas.validacionPorTipoTurno || CONFIGURACION_DEFAULT.reglas.validacionPorTipoTurno;
  const validacionSecuencial = reglas.validacionSecuencial || CONFIGURACION_DEFAULT.reglas.validacionSecuencial;
  const validacionRefrigerio = reglas.validacionRefrigerio || CONFIGURACION_DEFAULT.reglas.validacionRefrigerio;

  const getComment = (path) => {
    return REGLAS_HELP_TEXTS[path] || undefined;
  };

  return (
    <Form>
      <Row className="mb-3">
        <Col md={6}><Form.Group><Form.Label>Permitir ingreso después de salida <HelpTooltip id="rids" text={getComment("permitirIngresoDespuesDeSalida")} /></Form.Label><Form.Check type="switch" label=" " checked={!!reglas.permitirIngresoDespuesDeSalida} onChange={(e) => onUpdate("permitirIngresoDespuesDeSalida", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={6}><Form.Group><Form.Label>Permitir ingreso sin distancia <HelpTooltip id="risd" text={getComment("permitirIngresoSinDistancia")} /></Form.Label><Form.Check type="switch" label=" " checked={!!reglas.permitirIngresoSinDistancia} onChange={(e) => onUpdate("permitirIngresoSinDistancia", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}><Form.Group><Form.Label>Permitir salida sin distancia <HelpTooltip id="rssd" text={getComment("permitirSalidaSinDistancia")} /></Form.Label><Form.Check type="switch" label=" " checked={!!reglas.permitirSalidaSinDistancia} onChange={(e) => onUpdate("permitirSalidaSinDistancia", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={6}><Form.Group><Form.Label>Permitir refrigerio sin distancia <HelpTooltip id="rrsd" text={getComment("permitirRefrigerioSinDistancia")} /></Form.Label><Form.Check type="switch" label=" " checked={!!reglas.permitirRefrigerioSinDistancia} onChange={(e) => onUpdate("permitirRefrigerioSinDistancia", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h5 className="mt-4">Validación de Ubicación</h5>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Habilitada <HelpTooltip id="vuh" text={getComment("validacionUbicacion.habilitada")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionUbicacion.habilitada} onChange={(e) => onUpdate("validacionUbicacion.habilitada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Permitir Fuera de Área <HelpTooltip id="vupfa" text={getComment("validacionUbicacion.permitirFueraDeArea")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionUbicacion.permitirFueraDeArea} onChange={(e) => onUpdate("validacionUbicacion.permitirFueraDeArea", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Bloquear Fuera de Área <HelpTooltip id="vubfa" text={getComment("validacionUbicacion.bloquearFueraDeArea")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionUbicacion.bloquearFueraDeArea} onChange={(e) => onUpdate("validacionUbicacion.bloquearFueraDeArea", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Alertar Fuera de Área <HelpTooltip id="vuafa" text={getComment("validacionUbicacion.alertarFueraDeArea")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionUbicacion.alertarFueraDeArea} onChange={(e) => onUpdate("validacionUbicacion.alertarFueraDeArea", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h5 className="mt-4">Validación de Horarios</h5>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Habilitada <HelpTooltip id="vhh" text={getComment("validacionHorarios.habilitada")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionHorarios.habilitada} onChange={(e) => onUpdate("validacionHorarios.habilitada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Permitir Entrada Temprana <HelpTooltip id="vhpet" text={getComment("validacionHorarios.permitirEntradaTemprana")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionHorarios.permitirEntradaTemprana} onChange={(e) => onUpdate("validacionHorarios.permitirEntradaTemprana", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Permitir Entrada Tardía <HelpTooltip id="vhpeta" text={getComment("validacionHorarios.permitirEntradaTardia")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionHorarios.permitirEntradaTardia} onChange={(e) => onUpdate("validacionHorarios.permitirEntradaTardia", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Permitir Salida Temprana <HelpTooltip id="vhpst" text={getComment("validacionHorarios.permitirSalidaTemprana")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionHorarios.permitirSalidaTemprana} onChange={(e) => onUpdate("validacionHorarios.permitirSalidaTemprana", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>
      <Row className="mb-3">
        <Col md={4}><Form.Group><Form.Label>Permitir Salida Tardía <HelpTooltip id="vhpsta" text={getComment("validacionHorarios.permitirSalidaTardia")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionHorarios.permitirSalidaTardia} onChange={(e) => onUpdate("validacionHorarios.permitirSalidaTardia", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h6 className="mt-3">Validación por Tipo de Turno</h6>
      <Row className="mb-3">
        <Col md={4}><Form.Group><Form.Label>Habilitada <HelpTooltip id="vhvtth" text={getComment("validacionHorarios.validacionPorTipoTurno.habilitada")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionPorTipoTurno.habilitada} onChange={(e) => onUpdate("validacionPorTipoTurno.habilitada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label>Adaptar Horarios Según Turno <HelpTooltip id="vhvttahst" text={getComment("validacionHorarios.validacionPorTipoTurno.adaptarHorariosSegunTurno")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionPorTipoTurno.adaptarHorariosSegunTurno} onChange={(e) => onUpdate("validacionPorTipoTurno.adaptarHorariosSegunTurno", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label>Validar Duración Jornada <HelpTooltip id="vhvttvdj" text={getComment("validacionHorarios.validacionPorTipoTurno.validarDuracionJornada")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionPorTipoTurno.validarDuracionJornada} onChange={(e) => onUpdate("validacionPorTipoTurno.validarDuracionJornada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h5 className="mt-4">Validación Secuencial</h5>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Habilitada <HelpTooltip id="vsh" text={getComment("validacionSecuencial.habilitada")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionSecuencial.habilitada} onChange={(e) => onUpdate("validacionSecuencial.habilitada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Permitir Duplicados <HelpTooltip id="vspd" text={getComment("validacionSecuencial.permitirDuplicados")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionSecuencial.permitirDuplicados} onChange={(e) => onUpdate("validacionSecuencial.permitirDuplicados", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Validar Orden Lógico <HelpTooltip id="vsvol" text={getComment("validacionSecuencial.validarOrdenLogico")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionSecuencial.validarOrdenLogico} onChange={(e) => onUpdate("validacionSecuencial.validarOrdenLogico", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Usar Fecha Jornada <HelpTooltip id="vsufj" text={getComment("validacionSecuencial.usarFechaJornada")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionSecuencial.usarFechaJornada} onChange={(e) => onUpdate("validacionSecuencial.usarFechaJornada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Tiempo Mínimo Entre Marcaciones (min) <HelpTooltip id="vstmem" text={getComment("validacionSecuencial.tiempoMinimoEntreMarcaciones")} /></Form.Label><Form.Control type="number" value={validacionSecuencial.tiempoMinimoEntreMarcaciones || 0} onChange={(e) => onUpdate("validacionSecuencial.tiempoMinimoEntreMarcaciones", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Ventana Validación <HelpTooltip id="vsvv" text={getComment("validacionSecuencial.ventanaValidacion")} /></Form.Label><Form.Control type="text" value={validacionSecuencial.ventanaValidacion || ""} onChange={(e) => onUpdate("validacionSecuencial.ventanaValidacion", e.target.value)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Adaptar Según Tipo Turno <HelpTooltip id="vsastt" text={getComment("validacionSecuencial.adaptarSegunTipoTurno")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionSecuencial.adaptarSegunTipoTurno} onChange={(e) => onUpdate("validacionSecuencial.adaptarSegunTipoTurno", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h5 className="mt-4">Validación de Refrigerio</h5>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Habilitada <HelpTooltip id="vrh" text={getComment("validacionRefrigerio.habilitada")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionRefrigerio.habilitada} onChange={(e) => onUpdate("validacionRefrigerio.habilitada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Requiere Entrada Previa <HelpTooltip id="vrrep" text={getComment("validacionRefrigerio.requiereEntradaPrevia")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionRefrigerio.requiereEntradaPrevia} onChange={(e) => onUpdate("validacionRefrigerio.requiereEntradaPrevia", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Solo Un Refrigerio Por Jornada <HelpTooltip id="vrsurpj" text={getComment("validacionRefrigerio.soloUnRefrigerioPorJornada")} /></Form.Label><Form.Check type="switch" label=" " checked={!!validacionRefrigerio.soloUnRefrigerioPorJornada} onChange={(e) => onUpdate("validacionRefrigerio.soloUnRefrigerioPorJornada", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Tiempo Mínimo Trabajado (min) <HelpTooltip id="vrtmt" text={getComment("validacionRefrigerio.tiempoMinimoTrabajado")} /></Form.Label><Form.Control type="number" value={validacionRefrigerio.tiempoMinimoTrabajado || 0} onChange={(e) => onUpdate("validacionRefrigerio.tiempoMinimoTrabajado", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
      </Row>
    </Form>
  );
};

const FlujosMarcacionForm = ({ flujos, onUpdate, editable }) => {
  return (
    <Form>
      <h5 className="mb-3">Flujos de Marcación</h5>

      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Secuencia Normal</Form.Label>
            <JsonTextArea
              value={flujos.secuenciaNormal || CONFIGURACION_DEFAULT.flujosMarcacion.secuenciaNormal}
              onChange={(value) => onUpdate("secuenciaNormal", value)}
              disabled={!editable}
              rows={4}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Secuencia Sin Refrigerio</Form.Label>
            <JsonTextArea
              value={flujos.secuenciaSinRefrigerio || CONFIGURACION_DEFAULT.flujosMarcacion.secuenciaSinRefrigerio}
              onChange={(value) => onUpdate("secuenciaSinRefrigerio", value)}
              disabled={!editable}
              rows={4}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Permitir Saltar Refrigerio</Form.Label>
            <Form.Check
              type="switch"
              label=" "
              checked={!!flujos.permitirSaltarRefrigerio}
              onChange={(e) => onUpdate("permitirSaltarRefrigerio", e.target.checked)}
              disabled={!editable}
            />
          </Form.Group>
        </Col>
      </Row>

      <h6 className="mt-4 mb-3">Flujos por Tipo de Turno</h6>
      <Row>
        <Col md={12}>
          <Form.Group>
            <Form.Label>Configuración de Flujos por Tipo de Turno</Form.Label>
            <JsonTextArea
              value={flujos.flujosPorTipoTurno || CONFIGURACION_DEFAULT.flujosMarcacion.flujosPorTipoTurno}
              onChange={(value) => onUpdate("flujosPorTipoTurno", value)}
              disabled={!editable}
              rows={12}
            />
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

const MensajesForm = ({ mensajes, onUpdate, editable }) => {
  const campos = [
    { key: "bienvenida", label: "Bienvenida", placeholder: "Mensaje de bienvenida al registrar entrada" },
    { key: "despedida", label: "Despedida", placeholder: "Mensaje al registrar salida" },
    { key: "refrigerio", label: "Refrigerio", placeholder: "Mensaje al iniciar refrigerio" },
    { key: "firmaIngreso", label: "Firma Ingreso", placeholder: "Texto mostrado al firmar asistencia de ingreso" },
    { key: "firmaSalida", label: "Firma Salida", placeholder: "Texto mostrado al firmar asistencia de salida" },
    { key: "refrigerioRango", label: "Rango de Refrigerio", placeholder: "Ej: El refrigerio debe durar entre {min} y {max} minutos." },
    { key: "errorUbicacionNoDisponible", label: "Error Ubicación No Disponible", placeholder: "Mensaje cuando el GPS no está disponible o falla" },
    { key: "errorTurnoCompleto", label: "Error Turno Completo", placeholder: "Mensaje cuando se intenta marcar después de haber completado la jornada" },
    { key: "errorDescansoInsuficiente", label: "Error Descanso Insuficiente", placeholder: "Mensaje si no se ha cumplido el tiempo de descanso entre jornadas" },
  ];

  return (
    <Form>
      <h5 className="mb-3">Mensajes del Sistema</h5>
      <Row>
        {campos.map(({ key, label, placeholder }) => (
          <Col md={6} className="mb-3" key={key}>
            <Form.Group>
              <Form.Label>{label}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={mensajes[key] || ""}
                onChange={(e) => onUpdate(key, e.target.value)}
                disabled={!editable}
                placeholder={placeholder}
              />
            </Form.Group>
          </Col>
        ))}
      </Row>
    </Form>
  );
};

const MODO_OPTIONS = ["advertencia", "bloqueo", "ignorar"];

const ModoSelect = ({ value, onChange, disabled, id }) => (
  <Form.Control as="select" value={value || "advertencia"} onChange={(e) => onChange(e.target.value)} disabled={disabled} id={id}>
    {MODO_OPTIONS.map((m) => (
      <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
    ))}
  </Form.Control>
);

const BotonesForm = ({ botones, onUpdate, editable }) => {
  const botonKeys = ["Ingreso", "Salida", "Refrigerio_Inicio", "Refrigerio_Fin"];

  return (
    <Form>
      <h5 className="mb-3">Configuración de Botones</h5>
      <Row>
        {botonKeys.map((key) => {
          const btn = botones[key] || {};
          return (
            <Col md={6} className="mb-4" key={key}>
              <Card className="border">
                <Card.Header className="py-2 fw-semibold" style={{ backgroundColor: btn.color || "#ccc", color: "#fff" }}>
                  {key}
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-2">
                    <Form.Label>Etiqueta</Form.Label>
                    <Form.Control
                      type="text"
                      value={btn.label || ""}
                      onChange={(e) => onUpdate(`${key}.label`, e.target.value)}
                      disabled={!editable}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Color</Form.Label>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        value={btn.color || "#cccccc"}
                        onChange={(e) => onUpdate(`${key}.color`, e.target.value)}
                        disabled={!editable}
                        style={{ width: 48, height: 38, padding: 2 }}
                      />
                      <Form.Control
                        type="text"
                        value={btn.color || ""}
                        onChange={(e) => onUpdate(`${key}.color`, e.target.value)}
                        disabled={!editable}
                      />
                    </div>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Icono</Form.Label>
                    <Form.Control
                      type="text"
                      value={btn.icon || ""}
                      onChange={(e) => onUpdate(`${key}.icon`, e.target.value)}
                      disabled={!editable}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </Form>
  );
};

const LiteForm = ({ lite, onUpdate, editable }) => {
  const transiciones = lite.transiciones || {};
  const refrigerio = lite.refrigerio || {};
  const secuencia = lite.secuencia || {};
  const horarios = lite.horarios || {};
  const jornada = lite.jornada || {};
  const turnoNocturno = lite.turnos?.nocturno || {};
  const rolTurnos = lite.rolTurnos || {};

  return (
    <Form>
      <h5 className="mb-3">Motor Lite — Configuración de Modos</h5>

      <h6 className="mt-3">Transiciones Mínimas</h6>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Ingreso → Refrigerio (min)</Form.Label>
            <Form.Control type="number" value={transiciones.ingresoARefrigerio?.minutosMinimos ?? 120} onChange={(e) => onUpdate("transiciones.ingresoARefrigerio.minutosMinimos", Number(e.target.value))} disabled={!editable} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Fin Refri → Salida (min)</Form.Label>
            <Form.Control type="number" value={transiciones.refrigerioFinASalida?.minutosMinimos ?? 120} onChange={(e) => onUpdate("transiciones.refrigerioFinASalida.minutosMinimos", Number(e.target.value))} disabled={!editable} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Ingreso → Salida (min)</Form.Label>
            <Form.Control type="number" value={transiciones.ingresoASalida?.minutosMinimos ?? 480} onChange={(e) => onUpdate("transiciones.ingresoASalida.minutosMinimos", Number(e.target.value))} disabled={!editable} />
          </Form.Group>
        </Col>
      </Row>

      <h6 className="mt-3">Refrigerio</h6>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Mínimo legal (min)</Form.Label><Form.Control type="number" value={refrigerio.minimoLegalMinutos ?? 45} onChange={(e) => onUpdate("refrigerio.minimoLegalMinutos", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Máximo (min)</Form.Label><Form.Control type="number" value={refrigerio.maximoMinutos ?? 60} onChange={(e) => onUpdate("refrigerio.maximoMinutos", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Duración cero</Form.Label><ModoSelect id="ref_cero" value={refrigerio.duracionCeroModo} onChange={(v) => onUpdate("refrigerio.duracionCeroModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Menor al mínimo</Form.Label><ModoSelect id="ref_menor" value={refrigerio.menorAlMinimoModo} onChange={(v) => onUpdate("refrigerio.menorAlMinimoModo", v)} disabled={!editable} /></Form.Group></Col>
      </Row>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Mayor al máximo</Form.Label><ModoSelect id="ref_mayor" value={refrigerio.mayorAlMaximoModo} onChange={(v) => onUpdate("refrigerio.mayorAlMaximoModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Orden incorrecto</Form.Label><ModoSelect id="ref_orden" value={refrigerio.ordenModo} onChange={(v) => onUpdate("refrigerio.ordenModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Hora inicio sugerida</Form.Label>
            <Form.Control
              type="time"
              value={refrigerio.horaInicioSugerida || "12:00"}
              onChange={(e) => onUpdate("refrigerio.horaInicioSugerida", e.target.value)}
              disabled={!editable}
            />
          </Form.Group>
        </Col>
      </Row>

      <h6 className="mt-3">Secuencia</h6>
      <Row className="mb-3">
        <Col md={4}><Form.Group><Form.Label>Tipo incorrecto</Form.Label><ModoSelect id="sec_tipo" value={secuencia.tipoIncorrectoModo} onChange={(v) => onUpdate("secuencia.tipoIncorrectoModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label>Duplicado</Form.Label><ModoSelect id="sec_dup" value={secuencia.duplicadoModo} onChange={(v) => onUpdate("secuencia.duplicadoModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label>Orden lógico</Form.Label><ModoSelect id="sec_orden" value={secuencia.ordenLogicoModo} onChange={(v) => onUpdate("secuencia.ordenLogicoModo", v)} disabled={!editable} /></Form.Group></Col>
      </Row>
      <Row className="mb-3">
        <Col md={4}><Form.Group><Form.Label>Tiempo mínimo entre marcaciones</Form.Label><ModoSelect id="sec_tmin" value={secuencia.tiempoMinimoEntreMarcacionesModo} onChange={(v) => onUpdate("secuencia.tiempoMinimoEntreMarcacionesModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label>Salida sin refrigerio</Form.Label><ModoSelect id="sec_sref" value={secuencia.salidaSinRefrigerioModo} onChange={(v) => onUpdate("secuencia.salidaSinRefrigerioModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={4}><Form.Group><Form.Label className="d-block">Priorizar salida sobre refrigerio</Form.Label><Form.Check type="switch" label=" " checked={!!secuencia.priorizarSalidaSobreRefrigerio} onChange={(e) => onUpdate("secuencia.priorizarSalidaSobreRefrigerio", e.target.checked)} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h6 className="mt-3">Horarios</h6>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Entrada temprana</Form.Label><ModoSelect id="hor_et" value={horarios.entradaTempranaModo} onChange={(v) => onUpdate("horarios.entradaTempranaModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Entrada tardía</Form.Label><ModoSelect id="hor_eta" value={horarios.entradaTardiaModo} onChange={(v) => onUpdate("horarios.entradaTardiaModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Salida temprana</Form.Label><ModoSelect id="hor_st" value={horarios.salidaTempranaModo} onChange={(v) => onUpdate("horarios.salidaTempranaModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Salida tardía</Form.Label><ModoSelect id="hor_sta" value={horarios.salidaTardiaModo} onChange={(v) => onUpdate("horarios.salidaTardiaModo", v)} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h6 className="mt-3">Jornada</h6>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Mínima</Form.Label><ModoSelect id="jor_min" value={jornada.minimaModo} onChange={(v) => onUpdate("jornada.minimaModo", v)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Máxima</Form.Label><ModoSelect id="jor_max" value={jornada.maximaModo} onChange={(v) => onUpdate("jornada.maximaModo", v)} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h6 className="mt-3">Turno Nocturno</h6>
      <Row className="mb-3">
        <Col md={3}><Form.Group><Form.Label>Habilitado</Form.Label><Form.Check type="switch" label=" " checked={!!turnoNocturno.habilitado} onChange={(e) => onUpdate("turnos.nocturno.habilitado", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Aplicar a todos</Form.Label><Form.Check type="switch" label=" " checked={!!turnoNocturno.aplicarATodos} onChange={(e) => onUpdate("turnos.nocturno.aplicarATodos", e.target.checked)} disabled={!editable} /></Form.Group></Col>
        <Col md={3}><Form.Group><Form.Label>Hora Corte Jornada (h)</Form.Label><Form.Control type="number" value={turnoNocturno.horaCorteJornada ?? 12} onChange={(e) => onUpdate("turnos.nocturno.horaCorteJornada", Number(e.target.value))} disabled={!editable} /></Form.Group></Col>
      </Row>

      <h6 className="mt-3">Rol de Turnos</h6>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Habilitado</Form.Label>
            <Form.Check
              type="switch"
              label=" "
              checked={!!rolTurnos.habilitado}
              onChange={(e) => onUpdate("rolTurnos.habilitado", e.target.checked)}
              disabled={!editable}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Bloquear sin rol configurado</Form.Label>
            <Form.Check
              type="switch"
              label=" "
              checked={!!rolTurnos.bloquearSinRol}
              onChange={(e) => onUpdate("rolTurnos.bloquearSinRol", e.target.checked)}
              disabled={!editable}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Modo</Form.Label>
            <ModoSelect
              id="rol_turnos_modo"
              value={rolTurnos.modo || "bloqueo"}
              onChange={(v) => onUpdate("rolTurnos.modo", v)}
              disabled={!editable}
            />
          </Form.Group>
        </Col>
      </Row>
      <Alert variant="info" className="py-2">
        Cuando está habilitado, solo permite marcar en días laborales del rol (T/N). Códigos como D, M, V, F, PG, PS bloquean la marcación.
      </Alert>

      <PoliticasPorCentroForm
        politicas={lite.politicasPorCentro || {}}
        onUpdate={(nuevasPoliticas) => onUpdate("politicasPorCentro", nuevasPoliticas)}
        editable={editable}
      />
    </Form>
  );
};

// ─── Políticas por Centro ─────────────────────────────────────────────────────
const DIAS_SEMANA = [
  { key: "lunes",     label: "Lun" },
  { key: "martes",    label: "Mar" },
  { key: "miercoles", label: "Mié" },
  { key: "jueves",    label: "Jue" },
  { key: "viernes",   label: "Vie" },
  { key: "sabado",    label: "Sáb" },
  { key: "domingo",   label: "Dom" },
];

const POLITICA_FLAGS = [
  { key: "permitirSalidaSinRefrigerio", label: "Salida sin refrigerio" },
  { key: "permitirEntradaTemprana",     label: "Entrada temprana" },
  { key: "permitirSalidaTemprana",      label: "Salida temprana" },
  { key: "omitirValidacionUbicacion",   label: "Omitir validación GPS" },
];

const PoliticasPorCentroForm = ({ politicas, onUpdate, editable }) => {
  const [nuevoCentro, setNuevoCentro] = useState("");
  const centros = Object.keys(politicas || {});

  const agregarCentro = () => {
    const nombre = nuevoCentro.trim();
    if (!nombre || politicas[nombre] !== undefined) return;
    onUpdate({ ...politicas, [nombre]: {} });
    setNuevoCentro("");
  };

  const eliminarCentro = (nombre) => {
    const copia = { ...politicas };
    delete copia[nombre];
    onUpdate(copia);
  };

  const togglePolitica = (centro, dia, flag, valor) => {
    onUpdate({
      ...politicas,
      [centro]: {
        ...politicas[centro],
        [dia]: {
          ...politicas[centro]?.[dia],
          [flag]: valor,
        },
      },
    });
  };

  return (
    <div className="mt-4">
      <h6>Políticas por Centro de Trabajo</h6>
      <p className="text-muted small mb-3">
        Configura excepciones por día de la semana para centros específicos. El nombre debe coincidir exactamente con el nombre del centro en el sistema.
      </p>

      {editable && (
        <Row className="mb-3 align-items-end">
          <Col md={7}>
            <Form.Group>
              <Form.Label>Nombre del centro</Form.Label>
              <Form.Control
                type="text"
                value={nuevoCentro}
                onChange={(e) => setNuevoCentro(e.target.value)}
                placeholder="Ej: COOR_UO PKM_SEDE AREQUIPA"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarCentro(); } }}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Button
              variant="outline-primary"
              onClick={agregarCentro}
              disabled={!nuevoCentro.trim()}
            >
              + Agregar centro
            </Button>
          </Col>
        </Row>
      )}

      {centros.length === 0 && (
        <p className="text-muted fst-italic small">No hay políticas configuradas por centro.</p>
      )}

      {centros.map((centro) => (
        <Card className="mb-3 border" key={centro}>
          <Card.Header className="d-flex justify-content-between align-items-center py-2 bg-light">
            <span className="fw-semibold">{centro}</span>
            {editable && (
              <Button variant="outline-danger" size="sm" onClick={() => eliminarCentro(centro)}>
                Eliminar
              </Button>
            )}
          </Card.Header>
          <Card.Body className="p-2">
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0 text-center">
                <thead className="table-light">
                  <tr>
                    <th className="text-start" style={{ minWidth: 160 }}>Política</th>
                    {DIAS_SEMANA.map((d) => (
                      <th key={d.key}>{d.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {POLITICA_FLAGS.map((flag) => (
                    <tr key={flag.key}>
                      <td className="text-start">{flag.label}</td>
                      {DIAS_SEMANA.map((d) => (
                        <td key={d.key}>
                          <Form.Check
                            type="checkbox"
                            className="d-flex justify-content-center"
                            checked={!!politicas[centro]?.[d.key]?.[flag.key]}
                            onChange={(e) => togglePolitica(centro, d.key, flag.key, e.target.checked)}
                            disabled={!editable}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
};

export default ConfiguracionAsistencia;
